// src/components/bitacora/BitacoraPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Grid, Button, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Alert,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText,
  IconButton, Tooltip, Pagination
} from '@mui/material';
import {
  HistoryOutlined, FilterAltOffOutlined, InfoOutlined
} from '@mui/icons-material';
import { CardContainer, LoadingState } from '../shared';
import bitacoraService from '../../services/bitacoraService';

const ACCION_COLORS = {
  cargar:     'success',
  consultar:  'info',
  exportar:   'primary',
  crear:      'success',
  actualizar: 'warning',
  eliminar:   'error',
};

const formatFecha = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
};

const BitacoraPage = () => {
  const { empresaActiva } = useSelector(s => s.empresa);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [entidades, setEntidades] = useState([]);
  const [acciones, setAcciones] = useState([]);

  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState({
    entidad: '',
    accion: '',
    usuario_nombre: '',
    fecha_desde: '',
    fecha_hasta: '',
  });

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Cargar listas de filtros
  useEffect(() => {
    bitacoraService.listarEntidades().then(setEntidades).catch(() => {});
    bitacoraService.listarAcciones().then(setAcciones).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        page_size: 50,
        rut_empresa: empresaActiva?.rut || undefined,
        ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== '')),
      };
      const res = await bitacoraService.listar(params);
      setLogs(res.items || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
    } catch (e) {
      setError(e.message || 'Error al cargar bitácora');
    } finally {
      setLoading(false);
    }
  }, [page, filtros, empresaActiva]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleFiltro = (k, v) => {
    setFiltros(f => ({ ...f, [k]: v }));
    setPage(1);
  };

  const limpiarFiltros = () => {
    setFiltros({ entidad: '', accion: '', usuario_nombre: '', fecha_desde: '', fecha_hasta: '' });
    setPage(1);
  };

  const verDetalle = (log) => {
    setSelectedLog(log);
    setDetalleOpen(true);
  };

  return (
    <CardContainer
      title="Bitácora de Actividad"
      subheader="Historial de acciones realizadas en el sistema"
    >
      {/* Filtros */}
      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Entidad</InputLabel>
            <Select
              label="Entidad"
              value={filtros.entidad}
              onChange={e => handleFiltro('entidad', e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {entidades.map(e => (
                <MenuItem key={e} value={e}>{e}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Acción</InputLabel>
            <Select
              label="Acción"
              value={filtros.accion}
              onChange={e => handleFiltro('accion', e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {acciones.map(a => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <TextField
            size="small"
            label="Usuario"
            fullWidth
            value={filtros.usuario_nombre}
            onChange={e => handleFiltro('usuario_nombre', e.target.value)}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            size="small"
            label="Desde"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filtros.fecha_desde}
            onChange={e => handleFiltro('fecha_desde', e.target.value)}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            size="small"
            label="Hasta"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filtros.fecha_hasta}
            onChange={e => handleFiltro('fecha_hasta', e.target.value)}
          />
        </Grid>

        <Grid item xs="auto">
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterAltOffOutlined />}
            onClick={limpiarFiltros}
          >
            Limpiar
          </Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <LoadingState loading={loading}>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha/Hora</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Entidad</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>IP</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 40 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {formatFecha(log.created_at)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {log.usuario_nombre || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.accion}
                      color={ACCION_COLORS[log.accion] || 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.entidad}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{log.rut_empresa || '-'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {log.ip || '-'}
                  </TableCell>
                  <TableCell>
                    {log.detalle && (
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => verDetalle(log)}>
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {logs.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <HistoryOutlined sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No hay registros para los filtros seleccionados</Typography>
          </Box>
        )}

        {/* Paginación */}
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {total} registros
            </Typography>
            <Pagination
              count={pages}
              page={page}
              onChange={(_, v) => setPage(v)}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </LoadingState>

      {/* Dialog detalle */}
      <Dialog open={detalleOpen} onOpenChange={setDetalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryOutlined />
            Detalle del registro
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Fecha/Hora</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatFecha(selectedLog.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Usuario</Typography>
                  <Typography variant="body2">{selectedLog.usuario_nombre || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Acción</Typography>
                  <Typography variant="body2">{selectedLog.accion}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Entidad</Typography>
                  <Typography variant="body2">{selectedLog.entidad}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">RUT Empresa</Typography>
                  <Typography variant="body2">{selectedLog.rut_empresa || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">IP</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedLog.ip || '-'}
                  </Typography>
                </Grid>
              </Grid>

              {selectedLog.detalle && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Detalle</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: '#f5f5f5', overflowX: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.78rem' }}>
                      {JSON.stringify(selectedLog.detalle, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </CardContainer>
  );
};

export default BitacoraPage;
